import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, Check } from 'lucide-react';
import Button from '../../Button';
import BlurReveal from '../../BlurReveal';
import Row from './Row';
import FomoCard, { fomoShape } from './FomoCard';
import './HeroBannerV4.css';

/**
 * HeroBannerV4 — photo hero with a check-mark USP bar and a FOMO card
 * (Crown Student homepage).
 *
 * Figma (Crownstudent-MCP `HoZ60grGAGMTrlmGnOJxwV`): Desktop `90:26891`
 * (1600 wide: a 520px banner — the brand gradient with the cut-out photo over
 * its right half —, the text column 680px wide with a 420px wrapper, the
 * 72px USP bar under it, the 400px card right-aligned to the layout content
 * edge and straddling banner + bar), Mobile `96:30960` (text first on the
 * gradient, the cut-out 420px tall under it with the card on its lower part,
 * no CTA, the USP items in a 2×2 grid).
 *
 * - `title` (`typo-h1-editorial`) and `copy` in `--hero-v4-ink` (gray-0 — the
 *   Figma text sits white on the gradient); a `\n` in the title is an
 *   authored line break (Figma: «Ett minne / för livet!»).
 * - CTA Button (secondary, `ctaVariant` white by default, label in the brand
 *   primary-alternative, `ctaIcon` ArrowRight) — DESKTOP ONLY: the Figma
 *   mobile frame has no CTA (the card's button is the mobile call to action).
 * - `image` / `imageMobile` — the cut-out photo (PNG / WebP with alpha): on
 *   desktop it hangs over the banner's right half at the Figma geometry
 *   (`--hero-v4-figure-*`); on mobile it fills the photo block (cover,
 *   anchored to the top so a short screen crops it at the BOTTOM) and fades
 *   into gray-025 towards its bottom edge (CSS gradient, `--hero-v4-photo-fade`
 *   — the asset itself carries no fade). The field behind it is the component
 *   token `--hero-v4-bg` / `-bg-mobile` (Figma raw-hex gradients per brand,
 *   `sync-components.mjs`).
 * - **Mobile viewport fit** (like HeroBannerV3): the banner (text + photo with
 *   the card on it) fits the first screen under the host's sticky header, so
 *   the card's CTA is always on screen — the photo block takes the screen
 *   (`--hero-v4-band` = `max(100svh, --hero-v4-screen-h)` − `--hero-v4-offset`,
 *   the DS Header's `--header-height` by default) minus the measured text
 *   block (`--hero-v4-content-h`), never under `--hero-v4-photo-min`; a tall
 *   phone gets a taller photo, a short one a shorter one. Desktop keeps the
 *   fixed 520px banner.
 * - `usps` — `[{ label }]`, each a lucide Check + label; dividers on desktop.
 * - `fomo` — the card (see FomoCard): cap photo, delivery date, countdown,
 *   primary CTA. Rendered when present; the USP row then keeps clear of it
 *   (`hero-v4--with-card`). The card is a child of the banner, anchored to the
 *   photo's bottom edge on both breakpoints (desktop: hanging over the bar).
 * - `reveal` — the DS BlurReveal entrance: title (word by word) → copy → CTA
 *   → card → USP items, the photo fading in alongside (`REVEAL_TIMING`).
 *   Off by default.
 * - Every row renders only when it has content. One responsive component,
 *   single 1024px breakpoint. CSS hooks a host may set: `--hero-v4-banner-h`,
 *   `--hero-v4-offset`, `--hero-v4-photo-min`, `--hero-v4-photo-fade`,
 *   `--hero-v4-ink`, `--hero-v4-cta-color`,
 *   `--hero-v4-figure-h` / `-top` / `-center`, `--hero-v4-photo-position`,
 *   `--hero-v4-card-w`, `--hero-v4-card-gap`, `--hero-v4-card-bottom`,
 *   `--hero-v4-usp-h`, `--hero-v4-text-w`, `--hero-v4-wrapper-w`.
 */
const REVEAL_TIMING = { title: 0, copy: 144, photo: 192, cta: 288, card: 480, usps: 624 };
const PHOTO_DURATION = 960;
const TITLE_REVEAL = { unit: 'word', stagger: 38, duration: 480, distance: 'var(--space-ui-small-l)' };

const HeroBannerV4 = ({
  title = '',
  copy = '',
  ctaLabel = '',
  ctaIcon = <ArrowRight />,
  ctaVariant = 'white',
  ctaClassName = '',
  ctaHref,
  ctaOnClick,
  image,
  imageMobile,
  imageAlt = '',
  usps = [],
  fomo = null,
  reveal = false,
  className = '',
  ...rest
}) => {
  const hasCard = Boolean(fomo);
  const hasUsps = usps.length > 0;

  // Mobile sizing: the photo block takes what the screen leaves under the
  // text block (see HeroBannerV4.css), so the text block's height is measured
  // into `--hero-v4-content-h` (padding included — offsetHeight). A LAYOUT
  // effect: the first paint must already have it, or the photo block paints
  // one frame at the full band and jumps.
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setContentHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The screen the banner fills (`--hero-v4-screen-h`): CSS `100svh` alone is
  // not enough — Chrome on iOS reports it short of what it shows — so the CSS
  // takes the larger of the two. Read on mount and when the WIDTH changes
  // (rotation) only: height-only resizes are toolbars or a keyboard, and the
  // hero must not resize mid-scroll (the HeroBannerV3 rule).
  const [screenHeight, setScreenHeight] = useState(0);
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let width = window.innerWidth;
    setScreenHeight(window.innerHeight);
    const onResize = () => {
      if (window.innerWidth === width) return;
      width = window.innerWidth;
      setScreenHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // "\n" = an authored line break; each line is a block span (kept by BlurReveal).
  // The trailing space keeps the lines one sentence for assistive tech.
  const lines = title.split('\n');
  const titleContent = lines.map((line, i) => (
    <span key={i} className="hero-v4__title-line">{i < lines.length - 1 ? `${line} ` : line}</span>
  ));

  return (
    <section
      className={`hero-v4${hasCard ? ' hero-v4--with-card' : ''}${className ? ` ${className}` : ''}`}
      style={{
        ...(contentHeight ? { '--hero-v4-content-h': `${contentHeight}px` } : null),
        ...(screenHeight ? { '--hero-v4-screen-h': `${screenHeight}px` } : null),
        ...(rest.style || null),
      }}
      {...rest}
    >
      <div className="hero-v4__banner">
        <div className="hero-v4__container" ref={contentRef}>
          <div className="hero-v4__content">
            <div className="hero-v4__wrapper">
              {title && (reveal ? (
                <BlurReveal as="h1" delay={REVEAL_TIMING.title} className="hero-v4__title typo-h1-editorial" {...TITLE_REVEAL}>
                  {titleContent}
                </BlurReveal>
              ) : (
                <h1 className="hero-v4__title typo-h1-editorial">{titleContent}</h1>
              ))}

              {copy && (
                <Row reveal={reveal} delay={REVEAL_TIMING.copy} as="p" className="hero-v4__copy typo-body">
                  {copy}
                </Row>
              )}

              {ctaLabel && (
                <Row reveal={reveal} delay={REVEAL_TIMING.cta} className="hero-v4__cta">
                  <Button
                    size="secondary"
                    variant={ctaVariant}
                    className={ctaClassName}
                    iconRight={ctaIcon}
                    href={ctaHref}
                    onClick={ctaOnClick}
                  >
                    {ctaLabel}
                  </Button>
                </Row>
              )}
            </div>
          </div>
        </div>

        {image && (
          <Row reveal={reveal} delay={REVEAL_TIMING.photo} duration={PHOTO_DURATION} lift={false} className="hero-v4__photo">
            <picture className="hero-v4__picture">
              <source media="(min-width: 1024px)" srcSet={image} />
              <img className="hero-v4__media" src={imageMobile || image} alt={imageAlt} />
            </picture>
          </Row>
        )}

        {hasCard && <FomoCard fomo={fomo} reveal={reveal} delay={REVEAL_TIMING.card} />}
      </div>

      {hasUsps && (
        <div className="hero-v4__usp">
          <div className="hero-v4__usp-container">
            <Row reveal={reveal} delay={REVEAL_TIMING.usps} as="ul" className="hero-v4__usp-list">
              {usps.map(({ label }) => (
                <li key={label} className="hero-v4__usp-item">
                  <Check className="hero-v4__usp-check" size={24} strokeWidth={1.75} aria-hidden="true" />
                  <span className="hero-v4__usp-label typo-body">{label}</span>
                </li>
              ))}
            </Row>
          </div>
        </div>
      )}
    </section>
  );
};

HeroBannerV4.propTypes = {
  /** Headline (typo-h1-editorial, --hero-v4-ink); "\n" = an authored line break */
  title: PropTypes.string,
  /** Body copy under the headline (typo-body, --hero-v4-ink) */
  copy: PropTypes.string,
  /** CTA label (Button secondary, `ctaVariant`) — desktop only, the Figma mobile frame has no CTA */
  ctaLabel: PropTypes.string,
  /** CTA icon (ArrowRight by default) */
  ctaIcon: PropTypes.node,
  /** CTA Button variant (white by default) */
  ctaVariant: PropTypes.string,
  /** Extra class on the CTA (placement hooks; a skin is a `ctaVariant`) */
  ctaClassName: PropTypes.string,
  /** CTA link target — renders the CTA as a real link */
  ctaHref: PropTypes.string,
  /** CTA handler */
  ctaOnClick: PropTypes.func,
  /** Desktop photo — a cut-out with alpha hanging over the banner's right half (Figma geometry) */
  image: PropTypes.string,
  /** Mobile photo — the cut-out framed for the photo block, cover from the top (falls back to image) */
  imageMobile: PropTypes.string,
  /** Alt text for the photo */
  imageAlt: PropTypes.string,
  /** USP items under the banner: [{ label }] — each a check mark + label; [] hides the bar */
  usps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    }),
  ),
  /** The FOMO card { image, imageAlt, deliveryLabel, deliveryDate, countdownLabel, deadline, countdownUnits, ctaLabel, ctaHref, ctaOnClick, ctaVariant }; null hides it */
  fomo: fomoShape,
  /** Play the BlurReveal entrance (title word by word → copy → CTA → card → USP items; photo fading in alongside) */
  reveal: PropTypes.bool,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default HeroBannerV4;
